import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { IcBarChart, IcReceipt, IcReceiptText, IcWallet, IcTrophy, IcLayers, IcWarehouse, IcCoins, IcBanknote } from '../../theme/icons';
import PurchaseSalesBarChart from '../../components/PurchaseSalesBarChart';
import KI from '../../theme/palette';
import statisticsService from '../../services/statisticsService';
import { peekCached } from '../../services/apiCache';
import { fmt, Delta, StatCard, Section, StatsChip, Spinner, HBarList } from './statsUi';
import { StatsFilterBar, DEFAULT_FILTER } from './StatsFilter';

// Libellé de granularité du graphe Achats vs Ventes (renvoyée par l'API
// selon la période filtrée).
const STEP_LABELS = { day: '/ jour', week: '/ semaine', month: '/ mois' };

const GeneralStatistics = () => {
  // filter = { period } (période rapide) OU { start, end } (plage libre) —
  // même filtre que « Statistique de ventes » (owner 2026-08-16).
  const [filter, setFilter] = useState(DEFAULT_FILTER);
  // Last known answer painted at once; the fetch below refreshes it.
  const [data, setData] = useState(() => peekCached('statistics/', DEFAULT_FILTER.period ? { period: DEFAULT_FILTER.period } : undefined) || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Une seule grande carte, 3 vues (owner 2026-09-07, mêmes règles que Statistiques
  // de ventes) : compare (Achats vs Ventes) | products | categories
  const [mainView, setMainView] = useState('compare');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    statisticsService.getStatistics(filter.period || { start: filter.start, end: filter.end })
      .then((d) => { if (alive) { setData(d); setError(null); } })
      .catch(() => { if (alive) setError('Impossible de charger les statistiques.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [filter]);

  const s = data?.summary || {};
  const pr = data?.prev || {};
  const achats = data?.achats || {};
  const outstanding = data?.outstanding || {};
  const stock = data?.stock || {};
  const netPositive = (s.benefice_net || 0) >= 0;

  return (
    <div className="w-full px-4 pt-2 pb-4">
      <StatsChip
        icon={IcBarChart} title="Statistique générale"
        right={
          <Link
            to="/statistiques/historique-actions"
            className="btn-s px-4 py-2.5 rounded-xl transition-colors"
          >
            <IcReceipt className="text-sm" />
            Historique d'action
          </Link>
        }
      />

      <StatsFilterBar filter={filter} onChange={setFilter} />

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-4 mb-4 rounded-xl">{error}</div>}

      {loading && !data ? <Spinner /> : (
        <>
          {/* KPI — 6 cartes, même rangée que « Statistique de ventes »
              (ordre owner 2026-09-07, remplace le 2026-08-16) : CA → Stock
              (valeur | vente | alertes) → Bénéfice net → Crédits clients →
              Charges → Dette fournisseurs. Les achats de la période vivent
              en sous-ligne de la dette fournisseurs. */}
          {/* Toujours 2 rangées × 3 cases dès md (Ahmed 2026-08-16) — plus
              de rangée unique élargie en xl. */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
            {/* CA + coût des ventes dans la même carte (Ahmed 2026-08-16) :
                sous le montant, le capital marchandise vendu avec son % . */}
            {/* Cartes KPI blanches = information ; corail = crédit dû /
                bénéfice négatif (Composants-2 §5). */}
            {/* Owner 2026-09-10 : les 5 cartes (CA, Bénéfice net, Crédits
                clients, Charges, Dette fournisseurs) prennent la disposition
                de la case Stock (layout="stock") — icône + libellé centrés,
                valeur centrée, sous-ligne sous un filet — sans toucher aux
                couleurs. */}
            <StatCard icon={IcBanknote} label="Chiffre d'affaires" layout="stock"
              value={fmt(s.ca)} curr={s.ca} prev={pr.ca} valueClassName="text-green-900">
              {/* Libellé + montant + % sur UNE ligne (Ahmed 2026-08-16). */}
              <div className="flex items-baseline justify-center gap-x-2 text-[11px] min-w-0">
                <span className="text-gray-500 truncate">Capital marchandise vendu</span>
                <span className="shrink-0 font-semibold tabular-nums text-red-600">{fmt(s.capital)}</span>
                <span className="shrink-0"><Delta curr={s.capital} prev={pr.capital} invert /></span>
              </div>
            </StatCard>
            {/* Case Stock (layout Ahmed 2026-08-16) : icône + « Stock »
                centrés en haut, puis Valeur | Vente côte à côte, et une
                ligne de compteurs (articles · produits · ruptures) en bas.
                Marge potentielle + sous stock min conservés en infobulle. */}
            <div
              className="col-span-2 md:col-span-1 kpi min-w-0"
              title={`${stock.valeur_achat > 0
                ? `Marge potentielle +${(((stock.valeur_vente || 0) / stock.valeur_achat - 1) * 100).toFixed(0)}% · `
                : ''}${stock.stock_bas || 0} produit(s) sous le stock minimum`}
            >
              {/* Icône + « Stock » sur la même ligne, centrés (Ahmed). */}
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="h-7 w-7 shrink-0 rounded-lg flex items-center justify-center text-iface-600">
                  <IcWarehouse size={12} />
                </span>
                <span className="kpi-label uppercase">Stock</span>
              </div>
              {/* Owner 2026-09-10 : libellé et montant sur la MÊME ligne
                  (« Valeur 75 905 DT | Vente 210 288 DT ») pour que la case
                  Stock — et donc toute la rangée 1 (CA, Bénéfice net) — ait
                  la hauteur des cases Charges / Crédits / Dette. */}
              <div className="grid grid-cols-2 divide-x divide-gray-100 text-center">
                <div className="pr-3 min-w-0 flex items-baseline justify-center gap-1.5">
                  <span className="text-[11px] text-gray-500 shrink-0">Valeur</span>
                  <span className="text-lg font-bold tabular-nums truncate text-amber-600">{fmt(stock.valeur_achat)}</span>
                </div>
                <div className="pl-3 min-w-0 flex items-baseline justify-center gap-1.5">
                  <span className="text-[11px] text-gray-500 shrink-0">Vente</span>
                  <span className="text-lg font-bold tabular-nums truncate text-green-700">{fmt(stock.valeur_vente)}</span>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-gray-100 text-[11px] text-gray-500 text-center">
                {stock.qty_total || 0} article(s) · {stock.en_stock || 0} produit(s) en stock ·{' '}
                <Link to="/products" className="font-semibold text-red-600 hover:text-red-800">
                  {stock.rupture || 0} rupture(s)
                </Link>
              </div>
            </div>

            <StatCard icon={IcCoins} label="Bénéfice net" tone={netPositive ? 'white' : 'coral'} layout="stock"
              className={netPositive ? '' : 'cursor-default'}
              valueClassName={netPositive ? 'text-amber-700' : ''}
              value={fmt(s.benefice_net)} curr={s.benefice_net} prev={pr.benefice_net}>
              {/* Formule + lien forcés sur UNE ligne (Ahmed 2026-08-16) :
                  la formule se tronque si la carte est étroite, le lien
                  reste entier. */}
              <div className="flex items-baseline justify-center gap-x-2 text-[11px] min-w-0">
                <span className={`truncate ${netPositive ? 'text-gray-500' : 'text-red-800'}`}>CA − coût − charges</span>
                <Link to="/statistiques/ventes"
                  className={`shrink-0 font-semibold transition-colors ${
                    netPositive ? 'text-iface-600 hover:text-gray-900' : 'text-red-700 hover:text-red-900'
                  }`}>
                  Détail des ventes →
                </Link>
              </div>
            </StatCard>
            {/* Crédits clients à la place de l'ancienne carte Bénéfice net
                (Ahmed 2026-08-16). */}
            <StatCard icon={IcWallet} label="Crédits clients" className="cursor-default" layout="stock"
              value={fmt(outstanding.total)} valueClassName="text-red-700"
              sub={`${outstanding.count || 0} vente(s) à crédit — total`} />
            {/* Charges : TOUTES les charges (payées ou non), en ligne —
                « Salaire : 1100 DT · Inda : 1100 DT · … » (Ahmed 2026-08-16). */}
            <StatCard icon={IcReceiptText} label="Charges" layout="stock"
              value={fmt(s.charges)} curr={s.charges} prev={pr.charges} invert>
              {/* Sous le montant : les charges PAYÉES qui le composent, en
                  ligne continue « Salaire : 1100 · Inda : 1003 · … » comme la
                  formule du Bénéfice net — plus de grille 3 colonnes qui
                  tronquait les longs libellés et laissait de grands vides
                  (Ahmed 2026-08-16). */}
              {(data?.charges_detail || []).length > 0 ? (
                <div className="flex flex-wrap justify-center gap-x-2 gap-y-0.5 text-[11px] text-gray-500">
                  {data.charges_detail.map((c, i) => (
                    <span key={i} className="whitespace-nowrap">
                      {i > 0 && <span className="mr-2 text-gray-300">·</span>}
                      {c.name} : <span className="tabular-nums">{fmt(c.total)}</span>
                    </span>
                  ))}
                </div>
              ) : (
                <div className="text-[11px] text-gray-400">Aucune charge payée sur la période</div>
              )}
            </StatCard>

            {/* Dette fournisseurs seule — les crédits clients ont leur propre
                carte en rangée 1 (Ahmed 2026-08-16). */}
            <StatCard icon={IcReceiptText} label="Dette fournisseurs" layout="stock"
              value={fmt(achats.outstanding)} valueClassName="text-red-600">
              <div className="text-[11px] text-gray-500"
                title={`${achats.outstanding_count || 0} facture(s) impayée(s) · ${achats.count || 0} facture(s) sur la période`}>
                Achats {fmt(achats.total)} · payé {fmt(achats.paid)}
              </div>
            </StatCard>
          </div>

          {/* UNE grande carte, 3 vues au même endroit (owner 2026-09-07, comme la page
              Statistiques de ventes) : Achats vs Ventes (suit le filtre de période —
              Ahmed 2026-08-16 ; granularité jour/semaine/mois du backend), Top produits
              (achat), Top catégories (achat). Légende SOUS les barres comme au tableau
              de bord ; cadre à taille fixe ; bascule en pied de carte, style pagination. */}
          {(() => {
            const VIEWS = {
              compare:    { icon: IcBarChart, tone: 'blue',  title: `Achats vs Ventes ${STEP_LABELS[data?.compare_step] || ''}`.trim() },
              products:   { icon: IcTrophy,   tone: 'amber', title: 'Top produits (achat)' },
              categories: { icon: IcLayers,   tone: 'blue',  title: 'Top catégories (achat)' },
            };
            const v = VIEWS[mainView] || VIEWS.compare;
            const scrolls = mainView === 'products' || mainView === 'categories';
            return (
              <Section icon={v.icon} tone={v.tone} title={v.title}
                right={mainView === 'compare' ? (data?.label || '') : null}
                className="mb-4">
                {/* Cadre à TAILLE FIXE, sans mou : gabarit invisible = SVG du diagramme
                    (viewBox 1000×460 → largeur × 0,46) + sa légende (mb-7 = 28 px). Les
                    vues se posent dessus en absolute ; tableau et catégories y défilent. */}
                <div className="relative min-w-0 overflow-hidden">
                  <div aria-hidden="true" className="mb-7 pointer-events-none" style={{ aspectRatio: '1000 / 460' }} />
                  <div className={`absolute inset-0${scrolls ? ' overflow-y-auto' : ''}`}>
                    {mainView === 'categories' && (
                      <HBarList tone="blue" cols={2}
                        rows={(data?.top_achat_categories || []).map((c) => ({
                          name: c.name, value: c.montant, extra: `${c.qty || 0} article(s)`,
                        }))}
                        emptyText="Aucun achat sur la période."
                      />
                    )}
                    {mainView === 'products' && (
                      (!data?.top_achat_products || data.top_achat_products.length === 0) ? (
                        <p className="text-gray-400 text-sm text-center py-8">Aucun achat sur la période.</p>
                      ) : (
                        /* Tableau dense (Composants-2 §6) — survol GRIS comme la liste des
                           produits (owner 2026-09-07), plus de vert. */
                        <div className="overflow-x-auto border border-gray-200">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left">
                                <th className="th-ki py-2 w-8">#</th>
                                <th className="th-ki py-2">Produit</th>
                                <th className="th-ki py-2 text-right">Qté</th>
                                <th className="th-ki py-2 text-right">Montant</th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.top_achat_products.map((t, i) => (
                                <tr key={i} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-100 transition-colors">
                                  <td className="td-ki py-2 text-gray-400 tabular-nums">{i + 1}</td>
                                  <td className="td-ki py-2 pr-2 font-semibold text-gray-900 truncate max-w-[220px]">{t.name}</td>
                                  <td className="td-ki py-2 text-right text-gray-500 tabular-nums">{t.qty}</td>
                                  <td className="td-ki py-2 text-right font-semibold text-gray-900 tabular-nums">{fmt(t.montant)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )
                    )}
                    {(mainView === 'compare' || !VIEWS[mainView]) && (
                      <PurchaseSalesBarChart data={data?.weekly_compare} achatColor={KI.sapphire} light
                        granularity={data?.compare_step} legend="bottom" />
                    )}
                  </div>
                </div>
                {/* Bascule = pied de pagination de la liste des produits : bandeau pleine
                    largeur, filet haut, boutons pg-on / pg-btn à droite. */}
                <div className="mt-2 -mx-5 -mb-5 px-5 py-4 border-t border-gray-100 bg-white rounded-b-2xl flex items-center gap-3">
                  <div className="flex-1" />
                  <div className="inline-flex items-center gap-1" role="tablist" aria-label="Vue affichée">
                    {[
                      { key: 'compare', label: 'Achats vs Ventes' },
                      { key: 'products', label: 'Top produits' },
                      { key: 'categories', label: 'Catégories' },
                    ].map((o) => (
                      <button key={o.key} type="button" role="tab" aria-selected={mainView === o.key}
                        onClick={() => setMainView(o.key)}
                        className={`${mainView === o.key ? 'pg-on' : 'pg-btn'} w-auto px-3`}>
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              </Section>
            );
          })()}
        </>
      )}
    </div>
  );
};

export default GeneralStatistics;
